import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import withTableProps from 'HOC/types/withTable';
import { actions as modalActions } from 'redux/modal';
import { SendFileForm } from 'components/CommonForms';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Icon } from '@material-ui/core';
import { transformTableData } from 'utils';
import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getTrainingSession, downloadParticipantAssessmentResult } from 'http/trainingSessions';
import { getSessionsPastConfig } from './helpers/getSessionTablesConfig';
import { formatTrainingModuleLabel, formatTrainingPartLabel } from './helpers/formatTrainingLabels';
import { handleCertificateDownload } from 'utils/certificateDownloadHandler';
import SessionView from '../SessionView';

const useStyles = makeStyles(theme => ({
  btn: {
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:first-child': {
      marginRight: 20,
    },
    '&:last-child': {
      marginLeft: 20,
    },
  },
  btnDelete: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.contrastText,
  },
  btnWrapper: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    textAlign: 'center',
    '& .MuiDialog-paper': {
      width: '500px',
    },
  },
  wrapper: {
    textAlign: 'center',
  },
}));

const SessionsPastTable = ({ tableData, helpers, userId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = useStyles();
  const [downloadingCertificateId, setDownloadingCertificateId] = useState(null);
  const [downloadingAssessmentId, setDownloadingAssessmentId] = useState(null);

  const handleCloseModal = () => {
    dispatch(modalActions.closeModal());
  };

  const showSessionInfo = sessionInfo => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('students.sessionDetails'),
        },
        <SessionView session={sessionInfo} />,
      ),
    );
  };

  const [sessionDetailsState, fetchSessionDetails] = useAsyncFn(async sessionId => {
    const data = await getTrainingSession(sessionId);
    if (!data.error && !(data.statusCode && data.statusCode >= 400)) {
      showSessionInfo(data);
    }
    return data;
  });

  const handleShowSessionView = event => {
    const sessionId = event.currentTarget.dataset.key;
    fetchSessionDetails(sessionId);
  };

  const tableActions = [
    {
      type: 'icon',
      icon: 'remove_red_eye',
      onClick: handleShowSessionView,
    },
  ];

  const [addFileState, addFile] = useAsyncFn(async (sessionId, formData) => {
    const data = await helpers.setCertificate(sessionId, userId, formData);
    if (!data.error && !(data.statusCode && data.statusCode >= 400)) {
      handleCloseModal();
      tableData.handleForceUpdate();
    }
  });

  const handleAddCertificate = sessionId => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingDetails.addCertificateModalTitle'),
        },
        <SendFileForm
          fieldName="certificate"
          onSubmitForm={value => addFile(sessionId, value)}
          rejectAction={() => {
            dispatch(modalActions.closeModal());
          }}
          submitting={addFileState.loading}
          fileFormat=".pdf"
        />,
      ),
    );
  };

  const [deleteFileState, deleteFile] = useAsyncFn(async sessionId => {
    const data = await helpers.deleteCertificate(sessionId, userId);
    if (!data.error && !(data.statusCode && data.statusCode >= 400)) {
      handleCloseModal();
      tableData.handleForceUpdate();
    }
  });

  const [, downloadFile] = useAsyncFn(
    async (targetSessionId, targetUserId) => await helpers.downloadCertificate(targetSessionId, targetUserId),
  );

  const handleShowCertificate = async (sessionId, certificate) => {
    const previewUrl =
      typeof certificate === 'string'
        ? certificate
        : typeof certificate?.previewUrl === 'string'
        ? certificate.previewUrl
        : '';

    if (certificate && !previewUrl) {
      setDownloadingCertificateId(sessionId);
      try {
        if (
          await handleCertificateDownload({
            certificate,
            courseId: sessionId,
            userId,
            downloadCertificate: downloadFile,
            dispatch,
            t,
          })
        ) {
          tableData.handleForceUpdate();
          return;
        }
      } finally {
        setDownloadingCertificateId(null);
      }
    }

    const certificateUrl = previewUrl || null;
    const picture = certificateUrl ? (
      <img src={certificateUrl} width="200" height="300" alt="" />
    ) : (
      <Icon color="primary" style={{ fontSize: 150 }}>
        image
      </Icon>
    );

    dispatch(
      modalActions.openModal(
        {
          size: 'large',
          title: t('trainingDetails.certificateModalTitle'),
          customStyles: classes.modal,
        },
        <div className={classes.wrapper}>
          {picture}
          <div className={classes.btnWrapper}>
            <Button
              className={classes.btn}
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                downloadFile(sessionId, userId).then(() => tableData.handleForceUpdate());
                handleCloseModal();
              }}
            >
              {t('buttons.save')}
            </Button>
            <Button
              className={`${classes.btn} ${classes.btnDelete}`}
              variant="outlined"
              size="large"
              onClick={handleCloseModal}
            >
              {t('buttons.cancel')}
            </Button>
            <Button
              className={classes.btn}
              variant="contained"
              color="error"
              size="large"
              onClick={() => deleteFile(sessionId)}
            >
              {t('buttons.delete')}
            </Button>
          </div>
        </div>,
      ),
    );
  };

  const handleDownloadQuizAnswers = async sessionId => {
    setDownloadingAssessmentId(sessionId);
    try {
      await downloadParticipantAssessmentResult(sessionId, userId);
    } finally {
      setDownloadingAssessmentId(null);
    }
  };

  const tableConfig = getSessionsPastConfig({
    handleAddCertificate,
    handleShowCertificate,
    handleDownloadQuizAnswers,
    isDownloadingCertificate: id => String(downloadingCertificateId) === String(id),
    isDownloadingAssessment: id => String(downloadingAssessmentId) === String(id),
  });
  const rows = (Array.isArray(tableData.data) ? tableData.data : []).map(item => {
    const isPassed = item.participantStatus === 'completed' || item.buttonParams?.status === 'Passed';
    const isFailed = item.participantStatus === 'failed' || item.buttonParams?.status === 'Failed';
    const certificate = item.certificatePreviewUrl
      ? { previewUrl: item.certificatePreviewUrl }
      : item.hasCertificate
      ? {}
      : null;

    return {
      ...item,
      trainingModuleLabel: formatTrainingModuleLabel(item.trainingModuleCode, item.trainingModuleName),
      trainingPartLabel: formatTrainingPartLabel(item.trainingPartVersionCode, item.trainingMethod),
      expirationDate: item.expirationDate ?? item.evidenceExpiresAt ?? null,
      statusLabel: isPassed
        ? 'Passed'
        : isFailed
        ? 'Failed'
        : item.buttonParams?.status || item.participantStatus || '—',
      evidenceParams: {
        rowId: item.id,
        hasCertificate: Boolean(item.hasCertificate),
        hasAssessmentResult: Boolean(item.hasAssessmentResult),
        isPerformedWithSuccess: isPassed ? true : isFailed ? false : null,
        certificate,
        isMoodleCourse: Boolean(item.isMoodleSession),
      },
    };
  });
  const [columns, tableRows] = transformTableData(tableConfig, rows);
  const isLoading = addFileState.loading || deleteFileState.loading || sessionDetailsState.loading;

  return (
    <ModalBusyContainer busy={isLoading}>
      <AppTable
        columns={columns}
        data={tableRows}
        totalCount={tableData.totalCount}
        rowsPerPage={tableData.pageSize}
        isLazyLoading
        onChangePage={tableData.handleChangePage}
        onColumnSorting={tableData.handleSorting}
        actions={tableActions}
      />
    </ModalBusyContainer>
  );
};

SessionsPastTable.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
  helpers: PropTypes.shape({
    setCertificate: PropTypes.func,
    downloadCertificate: PropTypes.func,
    deleteCertificate: PropTypes.func,
  }).isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default SessionsPastTable;
